import React, {useState, useEffect} from 'react';
import { useHistory } from "react-router-dom";
import { RadioButton } from 'primereact/radiobutton';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { addDateTime } from '../../../common/date-time';
import { postIncidentSuppressionRequestInit, getDevicesByStoreCodeInit } from '../redux/actions';
import { getAllStoreListInit } from '../../search-store/redux/actions';

import "./IncidentSuppressionRequest.scss";

// TODO: replace with API calls for store list

const IncidentSuppressionRequest = () => {

    const incidentSuppressionTypes = [
        {label: "Temporary", value: "Temporary"},
        {label: "Permanent", value: "Permanent"},
        {label: "Usecase", value: "Usecase"},
    ]

    const suppressionScopeTypes = [
        {label: "Store Specific", value: "StoreSpecific"},
        {label: "Device Specific", value: "DeviceSpecific"},
    ]

    const deviceTypes = [
        {label: "Modem-WAN1", value: "modem-wan1"},
        {label: "Modem-WAN2", value: "modem-wan2"},
        {label: "CP", value: "cp"},
        {label: "AP", value: "ap"},
        {label: "Switch", value: "switch"},
    ]

    const UsecaseTypes = [
        {label: "Modem", value: "modem"},
        {label: "CP", value: "cp"},
        {label: "AP", value: "ap"},
        {label: "Switch", value: "switch"},
    ]

    const defaultState = {
        incidentSuppressionType: "Temporary",
        suppressionScope: "StoreSpecific",
        deviceType: [],
        stores: "",
        startDateTime: null,
        endDateTime: null,
        snowTicket: "",
        description: "",
        selectedStore: null,
        selectedDevices: [],
    }

    const [defaultValues, setDefaultValues] = useState(defaultState);
    const storeListFromRedux = useSelector(state => state.searchStore.storeList);
    const devicesByStoreCode = useSelector(state => state.incidentSuppression.devicesByStoreCode);
    const dispatch = useDispatch();
    const history = useHistory();
    const regex = /^\d{4}$/

    useEffect(() => {
        dispatch(getAllStoreListInit());
    }, [dispatch]);

    // Filter stores to only include 4-digit numeric stores
    const filteredStores = storeListFromRedux
        .filter(store => /^\d{4}$/.test(store.name))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(store => ({
            label: store.name,
            value: store.name
        }));

    // Transform devices to dropdown format
    const fixedDevices = [
        { label: "Modem-WAN1", value: "modem-wan1" },
        { label: "Modem-WAN2", value: "modem-wan2" },
        { label: "CP", value: "cp" }
    ];

    const deviceOptions = [
        ...fixedDevices,
        ...(devicesByStoreCode || []).map(device => ({
            label: device.assetName,
            value: device.assetName
        }))
    ];

    const {
        values,
        touched,
        handleChange,
        setFieldValue,
        handleSubmit,
        errors,
        resetForm
    } = useFormik({
        enableReinitialize: true,
        initialValues: defaultValues,
        validate: (data) => {
            let errors = {};
            const isUsecase = data.incidentSuppressionType === "Usecase";
            const isDeviceSpecific = data.suppressionScope === "DeviceSpecific";

            if (isUsecase) {
                validateDeviceType(data, errors, true);
            } else if (isDeviceSpecific) {
                validateSelectedStore(data, errors);
                validateSelectedDevices(data, errors);
            } else {
                validateDeviceType(data, errors, false);
                validateStores(data, errors);
            }
            validateStartDatetime(data, errors);
            validateEndDatetime(data, errors);
            validateSnowTicket(data, errors);
            validateDescription(data, errors);
            return errors;
        },
        onSubmit: (data) => {
            const isUsecase = data.incidentSuppressionType === "Usecase";
            const isDeviceSpecific = !isUsecase && data.suppressionScope === "DeviceSpecific";

            let payload;
            if (isDeviceSpecific) {
                // Build deviceType array and deviceNames string
                const deviceTypeSet = new Set();
                const deviceNames = [];

                data.selectedDevices.forEach(deviceValue => {
                    // Check if it's a fixed device
                    if (deviceValue === "modem-wan1") {
                        deviceNames.push("modem-wan1");
                        deviceTypeSet.add("modem-wan1");
                    } else if (deviceValue === "modem-wan2") {
                        deviceNames.push("modem-wan2");
                        deviceTypeSet.add("modem-wan2");
                    } else if (deviceValue === "cp") {
                        deviceNames.push("cp");
                        deviceTypeSet.add("cp");
                    } else {
                        // It's an API device - find its productType
                        const apiDevice = devicesByStoreCode.find(d => d.assetName === deviceValue);
                        if (apiDevice) {
                            deviceNames.push(apiDevice.assetName);
                            if (apiDevice.productType === "switch") {
                                deviceTypeSet.add("switch");
                            } else if (apiDevice.productType === "wireless") {
                                deviceTypeSet.add("ap");
                            }
                        }
                    }
                });

                payload = {
                    incidentSuppressionType: data.incidentSuppressionType,
                    stores: data.selectedStore,
                    deviceNames: deviceNames.join(","),
                    deviceType: Array.from(deviceTypeSet),
                    startDateTime: data.startDateTime,
                    endDateTime: data.incidentSuppressionType === "Permanent" ? null : data.endDateTime,
                    snowTicket: data.snowTicket,
                    description: data.description,
                };
            } else {
                const storesArray = isUsecase
                    ? null
                    : data.stores.split(/[,\n]/).map(store => store.trim()).filter(store => store !== "").join(",");
                payload = {
                    incidentSuppressionType: data.incidentSuppressionType,
                    stores: storesArray,
                    deviceType: data.deviceType,
                    deviceNames: null,
                    startDateTime: data.startDateTime,
                    endDateTime: data.incidentSuppressionType === "Permanent" ? null : data.endDateTime,
                    snowTicket: data.snowTicket,
                    description: data.description,
                };
            }
            dispatch(postIncidentSuppressionRequestInit(payload));
            clearForm();
            history.push("/admin/incident-suppression/suppression-logs");
        }
    })

    const radioButtonHandler = () => (e) => {
        setFieldValue("incidentSuppressionType", e.value);
    }

    const handleStoreChange = (storeCode) => {
        setFieldValue("selectedStore", storeCode);
        setFieldValue("selectedDevices", []);

        if (storeCode) {
            dispatch(getDevicesByStoreCodeInit(storeCode));
        }
    };

    const validateDeviceType = (data, errors, isUsecase) => {
        if (!data.deviceType || data.deviceType.length === 0) {
            errors.deviceType = isUsecase ? "Please select the Usecase(s)" : "Please select the Device Type(s)";
        }
    }

    const validateStores = (data, errors) => {
        if (!data.stores) {
            errors.stores = "Please enter the Store(s)";
        } else {
            const storesArray = data.stores.split(/[,\n]/).map(store => store.trim());
            const invalidStores = storesArray.filter(store => !store.match(regex));
            if (invalidStores.length > 0) {
                errors.stores = `Invalid store format: ${invalidStores.join(",")}. Store number should be 4 digits and store list should be comma separated or new line separated`;
            } else {
                const uniqueStores = new Set(storesArray);
                if (uniqueStores.size !== storesArray.length) {
                    errors.stores = "Duplicate stores are not allowed";
                }
            }
        }
    }

    const validateStartDatetime = (data, errors) => {
        if (!data.startDateTime) {
            errors.startDateTime = "Please select the Start Datetime";
        } else if (data.startDateTime <= addDateTime("minutes", 14, new Date())) {
            errors.startDateTime = "Start Datetime should be atleast 15 minutes from now";
        }
    }

    const validateEndDatetime = (data, errors) => {
        if (data.incidentSuppressionType !== "Permanent") {
            if (!data.endDateTime) {
                errors.endDateTime = "Please select the End Datetime";
            } else if (data.endDateTime < addDateTime("minutes", 30, data.startDateTime)) {
                errors.endDateTime = "End Datetime should be at least 30 minutes from start datetime";
            }
        }
    }

    const validateSnowTicket = (data, errors) => {
        if (!data.snowTicket) {
            errors.snowTicket = "Please enter the SNOW Ticket number";
        }
    }

    const validateDescription = (data, errors) => {
        if (!data.description) {
            errors.description = "Please enter the Description";
        }
        if (data.description.trim().length > 150) {
            errors.description = "Max of 150 characters allowed for description";
        }
    }

    const validateSelectedStore = (data, errors) => {
        if (!data.selectedStore) {
            errors.selectedStore = "Please select a Store";
        }
    }

    const validateSelectedDevices = (data, errors) => {
        if (!data.selectedDevices || data.selectedDevices.length === 0) {
            errors.selectedDevices = "Please select at least one Device";
        }
    }

    const clearForm = () => {
        resetForm();
    };

    const getMinDate = () => {
        let date = new Date();
        date.setSeconds(0);
        date.setMilliseconds(0);
        date.setMinutes(date.getMinutes() + 15);
        let minutes = date.getMinutes();
        let m = minutes % 15;
        if (m > 0) {
            date.setMinutes(minutes + 15 - m);
        }
        return date;
    }

    const isDeviceSpecific = values.suppressionScope === "DeviceSpecific";
    const isStoreSpecific = values.suppressionScope === "StoreSpecific";

    // Filter suppression types based on scope
    const availableSuppressionTypes = isDeviceSpecific
        ? incidentSuppressionTypes.filter(t => t.value !== "Usecase")
        : incidentSuppressionTypes;

  return (
    <div className="incident-suppression-container container-fluid">
      <p className="page-label">
        EIOT Incident Suppression
        <i className="pi pi-angle-right"/>
        Suppression Request
      </p>
      <form className="container-fluid" onSubmit={handleSubmit}>

        {/* Scope — Store Specific / Device Specific (TOP) */}
        <div className="row input-field">
            <div className="col-12 col-lg-2">
                <label>Scope</label>
            </div>
            <div className="col-12 col-lg-10">
                {suppressionScopeTypes.map(({label, value}) => (
                    <span key={value} style={{display: "flex", alignItems: "center"}}>
                        <RadioButton
                            name="suppressionScope"
                            value={value}
                            checked={values.suppressionScope === value}
                            onChange={(e) => {
                                setFieldValue("suppressionScope", e.value);
                                setFieldValue("deviceType", []);
                                setFieldValue("stores", "");
                                setFieldValue("selectedStore", null);
                                setFieldValue("selectedDevices", []);
                                // Reset suppression type when changing scope
                                if (e.value === "DeviceSpecific" && values.incidentSuppressionType === "Usecase") {
                                    setFieldValue("incidentSuppressionType", "Temporary");
                                }
                            }}
                        />
                        <span> {label} </span>
                    </span>
                ))}
            </div>
        </div>

        {/* Suppression Type — Temporary / Permanent / Usecase */}
        <div className="row input-field">
            <div className="col-12 col-lg-2">
                <label>Suppression Type</label>
            </div>
            <div className="col-12 col-lg-10">
                {availableSuppressionTypes.map(({label, value}) => (
                    <span key={value} style={{display: "flex", alignItems:"center"}}>
                        <RadioButton
                            name="incidentSuppressionType"
                            value={value}
                            checked={values.incidentSuppressionType === value}
                            onChange={radioButtonHandler()}
                        />
                        <span> {label} </span>
                    </span>
                ))}
            </div>
        </div>

        {/* Device Type(s) — Store Specific */}
        {isStoreSpecific && (
        <div className="row input-field">
            <div className="col-12 col-lg-2">
                <label>Device Type(s)</label>
            </div>
            <div className="col-12 col-lg-10">
                <span>
                <MultiSelect
                    name="deviceType"
                    options={deviceTypes}
                    optionLabel="label"
                    optionValue="value"
                    value={values.deviceType}
                    placeholder="Select Device Type"
                    onChange={handleChange}
                    filter
                />
                <br/>
                {!!touched.deviceType && errors.deviceType && <span className="p-error">{errors.deviceType}</span>}
                </span>
            </div>
        </div>
        )}

        {/* Usecase(s) — Store Specific + Usecase type */}
        {isStoreSpecific && values.incidentSuppressionType === "Usecase" && (
        <div className="row input-field">
            <div className="col-12 col-lg-2">
                <label>Usecase(s)</label>
            </div>
            <div className="col-12 col-lg-10">
                <span>
                <MultiSelect
                    name="deviceType"
                    options={UsecaseTypes}
                    optionLabel="label"
                    optionValue="value"
                    value={values.deviceType}
                    placeholder="Select Usecase"
                    onChange={handleChange}
                    filter
                />
                <br/>
                {!!touched.deviceType && errors.deviceType && <span className="p-error">{errors.deviceType}</span>}
                </span>
            </div>
        </div>
        )}

        {/* Store(s) textarea — Store Specific */}
        {isStoreSpecific && values.incidentSuppressionType !== "Usecase" && (
        <div className="row input-field">
            <div className="col-12 col-lg-2">
                <label>Store(s)</label>
            </div>
            <div className="col-12 col-lg-10">
                <span style={{width: "100%"}}>
                <InputTextarea
                    name="stores"
                    style={{height: "60%"}}
                    value={values.stores}
                    onChange={handleChange}
                />
                <br />
                <strong>Note: For multiple stores use ',' as separator</strong>
                <br/>
                {!!touched.stores && errors.stores && <span className="p-error">{errors.stores}</span>}
                </span>
            </div>
        </div>
        )}

        {/* Store dropdown — Device Specific */}
        {isDeviceSpecific && (
        <div className="row input-field">
            <div className="col-12 col-lg-2">
                <label>Store</label>
            </div>
            <div className="col-12 col-lg-10">
                <span>
                <Dropdown
                    name="selectedStore"
                    options={filteredStores}
                    optionLabel="label"
                    optionValue="value"
                    value={values.selectedStore}
                    placeholder="Select a Store"
                    onChange={(e) => handleStoreChange(e.value)}
                    filter
                    showClear
                />
                <br/>
                {!!touched.selectedStore && errors.selectedStore && <span className="p-error">{errors.selectedStore}</span>}
                </span>
            </div>
        </div>
        )}

        {/* Device(s) multiselect — Device Specific */}
        {isDeviceSpecific && (
        <div className="row input-field">
            <div className="col-12 col-lg-2">
                <label>Device(s)</label>
            </div>
            <div className="col-12 col-lg-10">
                <span>
                <MultiSelect
                    name="selectedDevices"
                    options={deviceOptions}
                    optionLabel="label"
                    optionValue="value"
                    value={values.selectedDevices}
                    placeholder={values.selectedStore ? "Select Device(s)" : "Select a store first"}
                    onChange={(e) => setFieldValue("selectedDevices", e.value)}
                    filter
                    disabled={!values.selectedStore || deviceOptions.length === 0}
                />
                <br/>
                {!!touched.selectedDevices && errors.selectedDevices && <span className="p-error">{errors.selectedDevices}</span>}
                </span>
            </div>
        </div>
        )}

        {/* Selected Devices Display — Device Specific */}
        {isDeviceSpecific && values.selectedDevices && values.selectedDevices.length > 0 && (
        <div className="row input-field">
            <div className="col-12 col-lg-2">
                <label>Selected Device(s)</label>
            </div>
            <div className="col-12 col-lg-10">
                <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', minHeight: '40px'}}>
                    {values.selectedDevices.map((deviceValue, index) => {
                        const deviceLabel = deviceOptions.find(opt => opt.value === deviceValue)?.label || deviceValue;
                        return (
                            <span key={index} style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                borderRadius: '16px',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}>
                                {deviceLabel}
                                <span
                                    style={{
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '16px'
                                    }}
                                    onClick={() => setFieldValue('selectedDevices', values.selectedDevices.filter((_, i) => i !== index))}
                                >
                                    ×
                                </span>
                            </span>
                        );
                    })}
                </div>
            </div>
        </div>
        )}

        {/* Start Datetime */}
        <div className="row input-field">
            <div className="col-12 col-lg-2">
                <label>Start Datetime</label>
            </div>
            <div className="col-12 col-lg-10">
                <span>
                <Calendar data-testid="calendar"
                    name="startDatetime"
                    value={values.startDateTime}
                    onChange={(e) => setFieldValue("startDateTime", e.value)}
                    minDate={getMinDate()}
                    stepMinute={15}
                    showTime
                    showIcon
                />
                <br/>
                {!!touched.startDateTime && errors.startDateTime && <span className="p-error">{errors.startDateTime}</span>}
                </span>
            </div>
        </div>

        {/* End Datetime — hidden for Permanent */}
        {values.incidentSuppressionType !== "Permanent" && (
        <div className="row input-field">
            <div className="col-12 col-lg-2">
                <label>End Datetime</label>
            </div>
            <div className="col-12 col-lg-10">
                <span>
                <Calendar data-testid="calendar"
                    name="endDatetime"
                    value={values.endDateTime}
                    stepMinute={15}
                    onChange={(e) => setFieldValue("endDateTime", e.value)}
                    minDate={addDateTime("minutes", 30, values.startDateTime)}
                    showTime
                    showIcon
                />
                <br/>
                {!!touched.endDateTime && errors.endDateTime && <span className="p-error">{errors.endDateTime}</span>}
                </span>
            </div>
        </div>
        )}

        {/* SNOW Ticket */}
        <div className="row input-field">
            <div className="col-12 col-lg-2">
                <label>SNOW Ticket #</label>
            </div>
            <div className="col-12 col-lg-10">
                <span style={{width: "100%"}}>
                <InputText
                    name="snowTicket"
                    value={values.snowTicket}
                    onChange={handleChange}
                />
                <br/>
                {!!touched.snowTicket && errors.snowTicket && <span className="p-error">{errors.snowTicket}</span>}
                </span>
            </div>
        </div>

        {/* Description */}
        <div className="row input-field">
            <div className="col-12 col-lg-2">
                <label>Description</label>
            </div>
            <div className="col-12 col-lg-10">
                <span style={{width: "100%"}}>
                <InputText
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                />
                <br/>
                {!!touched.description && errors.description && <span className="p-error">{errors.description}</span>}
                </span>
            </div>
        </div>

        <div className="row input-field">
            <div className='col-auto'>
                <Button label="Save" type="submit" style={{marginRight: "1.25rem"}}/>
            </div>
            <div className='col-auto'>
                <Button label="Clear" type="reset" onClick={clearForm} className="p-button-secondary"/>
            </div>
        </div>

      </form>
    </div>
  );
}

export default IncidentSuppressionRequest;
